/** Event-level storefront settings (inside the Event Setup section). */
import { useState } from "react";
import toast from "react-hot-toast";
import { createApolloClient } from "../../../../providers/ApolloClientFactory";
import { userStore } from "../../../../store/user";
import { Input } from "../../../../components/inputs/Input";
import PriceInput from "../../../../components/inputs/PriceInput";
import CustomSelect from "../../../../components/customSelect";
import { PrimaryButton } from "../../../../components/Button";
import TicketingItemHTML from "../../components/TickingItemHTML";
import { UPDATE_EVENT } from "../../../events/mutation";
import { PUBLIC_SITE_BASE, SetupData } from "../types";

const client = createApolloClient();
const { getState: getUserState } = userStore;

export const StorefrontStep = ({ data, refresh }: { data: SetupData; refresh: () => void }) => {
  const event = data.event!;
  const [slug, setSlug] = useState(event.ticketing_slug ?? "");
  const [processorId, setProcessorId] = useState<number | null>(event.ticketing_payment_processor_id ?? null);
  const [fee, setFee] = useState<number>((Number(event.ticketing_service_fee_per_order) || 0) / 100);
  const [surcharge, setSurcharge] = useState<string>(String(event.web_surcharge_percentage ?? 0));
  const [showRemaining, setShowRemaining] = useState<boolean>(event.ticketing_configurations?.show_remaining_cta !== false);
  const [logo, setLogo] = useState(event.ticketing_logo ?? "");
  const [background, setBackground] = useState(event.ticketing_background_url ?? "");
  const [description, setDescription] = useState<string>(event.description ?? "");
  const [saving, setSaving] = useState(false);

  // Every processor for the org, like the Event page. Most ticketing processors are also
  // flagged is_rfid, so filtering on that flag hid the selected one and left the dropdown blank.
  const processorOptions = data.processors
    .map((p) => ({ label: `${p.processor ?? p.payment_processor ?? "Processor"} #${p.id}${p.is_active ? "" : " (inactive)"}`, value: p.id }));

  const save = async () => {
    const cleanSlug = slug.trim().toLowerCase().replace(/\s+/g, "-");
    if (!cleanSlug) {
      toast.error("A public URL slug is required");
      return;
    }
    setSaving(true);
    try {
      await client.mutate({
        mutation: UPDATE_EVENT,
        variables: {
          id: event.id,
          input: {
            ticketing_slug: cleanSlug,
            ticketing_payment_processor_id: processorId,
            ticketing_service_fee_per_order: Math.round((Number(fee) || 0) * 100),
            web_surcharge_percentage: Number(surcharge) || 0,
            ticketing_configurations: { ...(event.ticketing_configurations ?? {}), show_remaining_cta: showRemaining },
            ticketing_logo: logo.trim() || null,
            ticketing_background_url: background.trim() || null,
            // Quill reports an empty editor as "<p><br></p>", so test the text without tags.
            description: description.replace(/<[^>]*>/g, "").trim() ? description : null,
            last_updated_by: getUserState().user?.id,
          },
        },
      });
      await client.clearStore();
      toast.success("Storefront saved");
      refresh();
    } catch (err) {
      toast.error("Failed to save storefront: " + err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-md p-4 flex flex-col gap-4">
      <div>
        <div className="text-sm font-semibold text-gray-900">Storefront</div>
        <div className="text-sm text-gray-600">What the public ticketing page needs before anything can sell. These fields also live on the Event page and apply to every ticket.</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input label="Public URL slug" name="ticketing_slug" value={slug} onChange={(v) => setSlug(String(v ?? ""))} required />
        <CustomSelect title="Payment processor" name="ticketing_payment_processor_id" isClearable options={processorOptions} value={processorId} onChange={(v) => setProcessorId(v ? Number(v) : null)} />
        <div>
          <span className="block text-sm font-medium text-gray-700">Public page</span>
          <div className="mt-2 text-sm">
            {slug.trim() ? (
              <a href={PUBLIC_SITE_BASE + slug.trim()} target="_blank" rel="noopener noreferrer">{PUBLIC_SITE_BASE.replace("https://", "") + slug.trim()}</a>
            ) : (
              <span className="text-gray-400">Set a slug to get an address.</span>
            )}
            <div className="text-xs text-gray-500 mt-1">Live once the event is active and this is saved.</div>
          </div>
        </div>
        <PriceInput label="Service fee per order" name="ticketing_service_fee_per_order" value={fee} onChange={(v) => setFee(v ?? 0)} />
        <Input label="Web surcharge % ( 3.5 = 3.5% )" name="web_surcharge_percentage" type="number" value={surcharge} onChange={(v) => setSurcharge(String(v ?? ""))} />
        <label className="flex items-center gap-2 text-sm text-gray-700 mt-7">
          <input type="checkbox" checked={showRemaining} onChange={(e) => setShowRemaining(e.target.checked)} />
          Show "N tickets remaining" on the public page
        </label>
        <Input label="Logo URL" name="ticketing_logo" value={logo} onChange={(v) => setLogo(String(v ?? ""))} />
        <Input label="Background image URL" name="ticketing_background_url" value={background} onChange={(v) => setBackground(String(v ?? ""))} />
        <div className="mt-7 flex items-center gap-3">
          {logo.trim() ? (
            <img src={logo.trim()} alt="Logo preview" className="h-12 w-20 object-contain rounded border border-gray-200 bg-white" />
          ) : (
            <div className="h-12 w-20 rounded border border-dashed border-gray-300 flex items-center justify-center text-[10px] text-gray-400">no logo</div>
          )}
          {background.trim() ? (
            <img src={background.trim()} alt="Background preview" className="h-12 w-20 object-cover rounded border border-gray-200" />
          ) : (
            <div className="h-12 w-20 rounded border border-dashed border-gray-300 flex items-center justify-center text-[10px] text-gray-400">no background</div>
          )}
          <div className="text-xs text-gray-500">
            Paste full https:// image links. The public page uses them directly, so the images must be hosted somewhere public. Leave blank for the Ronin logo and the stock background.
          </div>
        </div>
        <div className="md:col-span-3">
          <TicketingItemHTML content={description} setContent={setDescription} label="Ticketing event description (shows on the public ticketing page)" />
        </div>
      </div>
      <div className="flex justify-end">
        <PrimaryButton isDisabled={saving} onClick={save}>{saving ? "Saving…" : "Save storefront"}</PrimaryButton>
      </div>
    </div>
  );
};
